import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { AccountLink } from '../components/AccountLink'
import { ButtonLink, Label, PageTitle } from '../components/ui'
import { meQuery } from '../lib/queries'

// The copyright page (sc-293, designs/20261012-dmca-basics.md §5): where a
// rights holder sends a notice, what it must contain, and the repeat-infringer
// rule, which the US safe harbour (17 U.S.C. §512) wants told to users. The
// agent's address, phone and email are as Ryan registered them with the
// Copyright Office (sc-2105, 2026-11-04); the page must match the
// registration word for word. Ryan registered the service provider as Cat
// Bowl with catbowl.app as its website (sc-5242, 2026-11-04).
const UPDATED = 'November 4, 2026'

const AGENT = 'Cat Bowl, Copyright Agent'

const AGENT_ADDRESS = '2118 Wilshire Blvd Unit 1646, Santa Monica, CA 90403'

const AGENT_PHONE = '323-364-1122'

// Forwarded to Ryan by Cloudflare Email Routing on catbowl.app, as hello@ is.
const COPYRIGHT_EMAIL = 'dmca@catbowl.app'

export const Route = createFileRoute('/copyright')({
  // Anyone can read this page, signed in or not (as /privacy): a rights
  // holder is not a member.
  beforeLoad: ({ context }) => context.queryClient.ensureQueryData(meQuery).catch(() => undefined),
  component: Copyright,
})

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-2.5">
      <Label>{title}</Label>
      <div className="grid gap-2.5 text-[15px] text-muted">{children}</div>
    </section>
  )
}

function Copyright() {
  return (
    <div className="mx-auto grid w-full max-w-[560px] gap-5 px-4 pb-16 pt-[calc(env(safe-area-inset-top,0px)+24px)]">
      <div className="flex items-start justify-between gap-3">
        <PageTitle eyebrow="Copyright">Whose work it is, and how to have it removed</PageTitle>
        <AccountLink className="mt-1 flex-none" />
      </div>
      <p className="text-[15.5px]">Cat Bowl is a weekly cat photo contest for groups of friends. Members post their own photos and videos, and only the members of a league see them. Only post what you made or may share. If something of yours was posted here without your permission, tell us and we will take it down.</p>
      <Section title="Send a notice">
        <p>Write to <b className="font-semibold text-ink">{AGENT}</b> at <a href={`mailto:${COPYRIGHT_EMAIL}`} className="font-semibold text-ink underline underline-offset-2">{COPYRIGHT_EMAIL}</a>, by post at {AGENT_ADDRESS}, or by phone at <a href={`tel:+1${AGENT_PHONE.replaceAll('-', '')}`} className="font-semibold text-ink underline underline-offset-2">{AGENT_PHONE}</a>.</p>
        <p>A notice under the US Digital Millennium Copyright Act needs all of these:</p>
        <ul className="grid list-disc gap-1.5 pl-5">
          <li>The work that you say was copied.</li>
          <li>What was posted here and where it is: a link, or the league, the person and the week.</li>
          <li>Your name, postal address, phone number and email address.</li>
          <li>A statement that you believe in good faith that the use is not allowed by the owner, the owner's agent or the law.</li>
          <li>A statement that the notice is accurate and, under penalty of perjury, that you are the owner or may act for the owner.</li>
          <li>Your signature. A typed name will do.</li>
        </ul>
      </Section>
      <Section title="What we do with it">
        <p>We remove the post, and we tell the person who posted it that it was removed for copyright, who sent the notice, and how to object.</p>
      </Section>
      <Section title="If your post was removed">
        <p>If you believe that was a mistake, send a counter-notice to the same address. It needs what was removed and where it was; your name, postal address and phone number; a statement under penalty of perjury that you believe it was removed by mistake or misidentification; your consent to the jurisdiction of the federal court for your district (or, outside the United States, any district where we can be found) and to being served by the person who sent the notice; and your signature.</p>
        <p>We send it on to the person who sent the notice. Unless they tell us within 10 to 14 business days that they have gone to court, the post may go back up.</p>
      </Section>
      <Section title="Repeat infringers">
        <p>An account that has had posts removed on two notices that stood is closed, and its email address may not sign up again. A notice that is undone by a counter-notice does not count.</p>
      </Section>
      <p className="text-[13px] text-muted">Last updated {UPDATED}.</p>
      <div className="flex flex-wrap gap-2.5">
        <ButtonLink to="/" size="sm">Back to the league</ButtonLink>
        <ButtonLink to="/privacy" size="sm">Privacy</ButtonLink>
        <ButtonLink to="/terms" size="sm">Terms</ButtonLink>
      </div>
    </div>
  )
}
